/**
 * The Add Part flow, wired to `POST /garages/:id/items` (owner only).
 *
 * The interesting work is `toRequest` at the bottom: the mock's toggles have to
 * become `CreateItemRequest`, and that contract carries cross-field rules the
 * form has to respect or every submit is a 422:
 *
 *  - **at least one trigger** — unless the part is a *catalog* item (below) or
 *    safety gear, which comes due on its recert / expiration date instead (the
 *    same as a driver's gear); a plain wear part with none can never come due;
 *  - **the unit must suit the type** — `UNITS_BY_TRIGGER` says a `time` trigger
 *    is days/weeks/months and nothing else, so the form's unit picker is driven
 *    from that constant rather than a copy of it;
 *  - **a time trigger needs `lastServicedAt`** — with nothing to count from it
 *    would project nothing and look like a broken part;
 *  - **only safety gear carries `expirationDate`**.
 *
 * The form mirrors those rules so the button is disabled rather than the request
 * rejected — but the API re-checks all of them, and its `issues` are what get
 * rendered against the fields. This is a convenience layer over the contract,
 * never a substitute for it.
 *
 * Two axes of generality live here, and they are independent:
 *
 *  - **Who owns the part.** The same sheet adds a wear part to a *car* and a
 *    piece of safety gear to a *driver* — both are items, differing only in
 *    `ownerType`/`ownerId` (routes/items.ts). The `owner` prop carries that, so
 *    the flow never has to know which screen opened it.
 *  - **Whether it is tracked.** A *catalog* item (a sensor, a bracket) is
 *    recorded only so its part number and buy link are on hand when it fails —
 *    it has no check conditions and no expiration, and never comes due. The
 *    contract already allows `triggers: []`; catalog mode is the UI that reaches
 *    it, hiding every field that would otherwise be required.
 */

import { useState } from 'react';
import { Bell, CalendarDays, CircleDashed, Flag, Gauge, Package, ShieldCheck, Timer, Trash2, Zap } from 'lucide-react';

import { UNITS_BY_TRIGGER } from '@svcdue/contracts';
import type { CreateItemRequest, ItemDto, OwnerType, System, TriggerInput, UpdateItemRequest } from '@svcdue/contracts';

import { useCreateItem, useDeleteItem, useUpdateItem } from '../api/hooks';
import { ApiError } from '../api/client';
import { errorMessage } from '../components/states';
import { Field, Opts, SheetHead, TrigToggle } from '../components/form';
import { today } from '../lib/format';

const SYS_OPTS: System[] = ['Engine', 'Power adder', 'Fuel', 'Driveline', 'Brakes', 'Chassis', 'Safety'];

/** Straight from the contract, so the picker can never offer an invalid pair. */
const TIME_UNITS = UNITS_BY_TRIGGER.time;

/**
 * Who a part hangs off. A car or a driver, plus the label to headline the sheet
 * with — the caller always has the entity in hand (the car row, the driver
 * screen), so `ownerId` never has to be recovered from anywhere else.
 */
export interface Owner {
  ownerType: OwnerType;
  ownerId: string;
  label: string;
}

interface TriggerState {
  on: boolean;
  val: string;
  unit?: string;
}

export interface PartForm {
  /**
   * Catalog mode: no check conditions, no safety, never tracked. It flips the
   * whole lower half of the form off and makes `triggers: []` a valid submit.
   */
  catalog: boolean;
  name: string;
  pn: string;
  sys: System;
  last: string;
  buy: string;
  safety: boolean;
  expiry: string;
  spec: string;
  triggers: Record<string, TriggerState>;
}

const EMPTY_TRIGGERS: Record<string, TriggerState> = {
  miles: { on: false, val: '3000' },
  time: { on: false, val: '6', unit: 'months' },
  passes: { on: false, val: '30' },
  events: { on: false, val: '1' },
  inspect: { on: false, val: '30' },
};

/**
 * The form's toggles → the contract's triggers.
 *
 * The key mapping is `miles → mileage`: the UI says "Mileage" and the engine's
 * type is `mileage`, but the form key is `miles`, and getting that wrong is a
 * silent 422. Exported and pure so a test can catch exactly that.
 */
export function toTriggers(triggers: Record<string, TriggerState>): TriggerInput[] {
  const out: TriggerInput[] = [];
  const value = (key: string): number => Number(triggers[key]?.val ?? 0);

  if (triggers.miles?.on) out.push({ type: 'mileage', intervalValue: value('miles'), unit: 'mi' });
  if (triggers.time?.on) {
    out.push({
      type: 'time',
      intervalValue: value('time'),
      unit: (triggers.time.unit ?? 'months') as (typeof TIME_UNITS)[number],
    });
  }
  if (triggers.passes?.on) out.push({ type: 'passes', intervalValue: value('passes'), unit: 'passes' });
  if (triggers.events?.on) out.push({ type: 'events', intervalValue: value('events'), unit: 'events' });
  // An inspect trigger is always measured in days (`UNITS_BY_TRIGGER.inspect`),
  // and is the only type allowed to carry `lastCheckedAt`.
  if (triggers.inspect?.on) out.push({ type: 'inspect', intervalValue: value('inspect'), unit: 'days' });

  return out;
}

/**
 * The fields shared by create and update — everything the form owns except the
 * `ownerType`/`ownerId`, which are set once at creation and never edited (a part
 * cannot be moved to another owner). `updateItemRequest` is a superset-shaped
 * partial, so sending the whole body on an edit is valid and re-checks the same
 * cross-field rules server-side.
 *
 * Catalog mode zeroes the tracked half wholesale: no triggers (the contract now
 * allows the empty array), never safety, so no expiration — which also means the
 * one field the contract forbids on a non-safety item can never leak out.
 */
function toBody(form: PartForm) {
  const text = (value: string): string | null => (value.trim() ? value.trim() : null);
  const catalog = form.catalog;
  const safety = !catalog && form.safety;
  return {
    name: form.name.trim(),
    partNumber: text(form.pn),
    system: form.sys,
    buyUrl: text(form.buy),
    isSafety: safety,
    // The contract rejects an expiration on a non-safety item outright, so it
    // is only ever sent when the safety toggle is on (and never in catalog mode).
    expirationDate: safety ? text(form.expiry) : null,
    spec: safety ? text(form.spec) : null,
    // A catalog item is not counted from anything, so there is nothing to anchor.
    lastServicedAt: catalog ? null : text(form.last),
    // The one field catalog mode is *for*: an item with no check conditions,
    // which the engine handles by projecting nothing.
    triggers: catalog ? [] : toTriggers(form.triggers),
  };
}

export function toRequest(form: PartForm, owner: Owner): CreateItemRequest {
  // ownerType/ownerId are only set at creation — a part belongs to its owner,
  // car or driver, and cannot be moved.
  return { ownerType: owner.ownerType, ownerId: owner.ownerId, ...toBody(form) };
}

export function toUpdate(form: PartForm): UpdateItemRequest {
  return toBody(form);
}

/**
 * A stored item → the form. The reverse of `toBody`, for the edit path.
 *
 * The subtlety is the trigger mapping: the engine stores an array of typed
 * triggers, and the form is a fixed set of toggles keyed by the *UI* name — so
 * `mileage` unpacks back to the `miles` toggle (the same non-identity mapping
 * `toTriggers` applies going the other way, and the same silent-422 risk if it
 * is wrong). A trigger the item does not carry keeps its default value but stays
 * off, so turning it on later starts from a sensible number rather than blank.
 *
 * Catalog mode is *inferred*, not stored: an item with no triggers and no safety
 * flag is a catalog entry, and reopening it in catalog mode is the only way the
 * sheet round-trips it without demanding a check condition it never had.
 */
export function itemToForm(item: ItemDto): PartForm {
  const triggers: Record<string, TriggerState> = {
    miles: { ...EMPTY_TRIGGERS.miles! },
    time: { ...EMPTY_TRIGGERS.time! },
    passes: { ...EMPTY_TRIGGERS.passes! },
    events: { ...EMPTY_TRIGGERS.events! },
    inspect: { ...EMPTY_TRIGGERS.inspect! },
  };

  for (const t of item.triggers) {
    // The one non-identity key: the engine's `mileage` is the form's `miles`.
    const key = t.type === 'mileage' ? 'miles' : t.type;
    const slot = triggers[key];
    if (slot) {
      slot.on = true;
      slot.val = t.intervalValue.toString();
      // Only the time trigger carries a user-chosen unit; the rest are fixed.
      if (t.type === 'time') slot.unit = t.unit;
    }
  }

  return {
    catalog: item.triggers.length === 0 && !item.isSafety,
    name: item.name,
    pn: item.partNumber ?? '',
    sys: item.system,
    last: item.lastServicedAt ?? '',
    buy: item.buyUrl ?? '',
    safety: item.isSafety,
    expiry: item.expirationDate ?? '',
    spec: item.spec ?? '',
    triggers,
  };
}

/** The rules the submit button mirrors, so it disables rather than 422s. */
export function formIssues(form: PartForm): string[] {
  const issues: string[] = [];

  if (!form.name.trim()) issues.push('A part needs a name.');
  // A catalog item is name + system only — the contract asks for nothing else,
  // and the check-condition rules below do not apply to a part with no conditions.
  if (form.catalog) return issues;

  const triggers = toTriggers(form.triggers);
  // Safety gear comes due on its recert / expiration date, so a check condition
  // is optional — exactly like a driver's gear (AddGear), which carries none. A
  // plain wear part still needs one: with no condition and no expiry to count
  // down to, it could never come due.
  if (!form.safety && triggers.length === 0) issues.push('Pick at least one check condition.');
  if (triggers.some((t) => t.intervalValue < 1)) issues.push('Every interval must be at least 1.');
  if (triggers.some((t) => t.type === 'time') && !form.last.trim()) {
    issues.push('A time-based interval needs a last-serviced date to count from.');
  }
  if (form.safety && !form.expiry.trim()) issues.push('Safety gear needs an expiration date.');

  return issues;
}

/**
 * One flow, many jobs: add a part or edit one, on a car or a driver, tracked or
 * catalog — the same shape as the car, event and driver sheets. Create and edit
 * share every field, the live rule preview and `toBody`; editing runs a PATCH
 * instead of a POST and can also delete. The `owner` is always in hand (the sheet
 * is opened from a car's part row or a driver's gear card), so `ownerId` never
 * has to be recovered from the item.
 */
export function AddPart({
  garageId,
  owner,
  item,
  onClose,
}: {
  garageId: string;
  owner: Owner;
  item?: ItemDto;
  onClose: () => void;
}) {
  const editing = item !== undefined;
  const [p, setP] = useState<PartForm>(
    item
      ? itemToForm(item)
      : {
          catalog: false,
          name: '',
          pn: '',
          sys: 'Engine',
          last: today(),
          buy: '',
          safety: false,
          expiry: '',
          spec: '',
          triggers: EMPTY_TRIGGERS,
        },
  );

  const create = useCreateItem(garageId);
  // Hooks are unconditional; the unused one for the current mode never fires.
  const update = useUpdateItem(garageId, item?.id ?? '');
  const del = useDeleteItem(garageId);
  const save = editing ? update : create;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = <K extends keyof PartForm>(k: K, v: PartForm[K]) => setP((s) => ({ ...s, [k]: v }));

  const t = p.triggers;
  const tog = (k: string) => set('triggers', { ...t, [k]: { ...t[k]!, on: !t[k]!.on } });
  const sv = (k: string, v: string) => set('triggers', { ...t, [k]: { ...t[k]!, val: v } });
  const su = (k: string, v: string) => set('triggers', { ...t, [k]: { ...t[k]!, unit: v } });

  const lines: string[] = [];
  if (t.miles!.on) lines.push(`every ${t.miles!.val || '—'} mi`);
  if (t.time!.on) lines.push(`every ${t.time!.val || '—'} ${t.time!.unit}`);
  if (t.passes!.on) lines.push(`every ${t.passes!.val || '—'} passes`);
  if (t.events!.on) lines.push(`every ${t.events!.val || '—'} event${t.events!.val === '1' ? '' : 's'}`);

  const metricCount = ['miles', 'time', 'passes', 'events'].filter((k) => t[k]!.on).length;
  const metricRule = lines.length ? lines.join(' · or ') + (metricCount > 1 ? ' — whichever first' : '') : null;
  const inspectRule = t.inspect!.on ? `inspect every ${t.inspect!.val || '—'} days` : null;

  const issues = formIssues(p);
  const issue = (path: string) => (save.error instanceof ApiError ? save.error.issueFor(path) : undefined);
  const busy = save.isPending || del.isPending;
  const submit = () => {
    if (issues.length > 0 || busy) return;
    if (editing) update.mutate(toUpdate(p), { onSuccess: onClose });
    else create.mutate(toRequest(p, owner), { onSuccess: onClose });
  };
  const remove = () => {
    if (!item || busy) return;
    del.mutate(item.id, { onSuccess: onClose });
  };

  return (
    <div className="sheet">
      <SheetHead
        title={editing ? 'Edit part' : 'Add part'}
        sub={owner.label}
        onClose={onClose}
        onSave={submit}
        saveDisabled={issues.length > 0 || busy}
        saveLabel={save.isPending ? 'Saving…' : editing ? 'Save' : 'Add'}
      />
      <div className="sheet-body">
        {/* The mode switch leads, because it decides what the rest of the form
            even asks for. A catalog item is recorded for its part number and buy
            link alone — it is never tracked, so the whole check-condition and
            safety half below simply disappears. */}
        <div className="catalog-wrap">
          <TrigToggle
            icon={Package}
            name="Catalog / reference only"
            sub="Not tracked — kept for its part number and buy link, so a failure is easy to reorder"
            on={p.catalog}
            onToggle={() => set('catalog', !p.catalog)}
            noInput
          />
        </div>

        <div className="form-sec">Part</div>
        <div className="field-grid-2">
          <Field label="Part name">
            <input className="input" value={p.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g., Blower belt" />
            {issue('name') && <div className="field-err">{issue('name')}</div>}
          </Field>
          <Field label="Part number" hint="optional">
            <input className="input" value={p.pn} onChange={(e) => set('pn', e.target.value)} placeholder="e.g., BDS 6483" />
          </Field>
        </div>
        <Field label="Buy URL" hint="optional — for one-tap reorder">
          <input
            className="input"
            type="url"
            inputMode="url"
            value={p.buy}
            onChange={(e) => set('buy', e.target.value)}
            placeholder="https://… where you reorder it"
          />
          {/* The contract only allows http(s) — `javascript:` must never reach
              an href (contracts/primitives.ts). */}
          {issue('buyUrl') && <div className="field-err">{issue('buyUrl')}</div>}
        </Field>
        <Field label="System">
          <Opts options={SYS_OPTS} value={p.sys} onChange={(v) => set('sys', v as System)} />
        </Field>

        {/* Everything below is the *tracked* half of the form. In catalog mode it
            is gone, not disabled: a reference part has no check condition, no
            starting point to count from, and no expiration, so offering those
            fields would only invite an invalid submit. */}
        {p.catalog ? (
          <div className="catalog-note">
            <Package size={13} /> Kept for reference only. No forecast, no reminders — just its part number and
            buy link, on hand for when it fails.
          </div>
        ) : (
          <>
            <div className="form-sec">
              Check conditions <span className="sec-hint">turn on any that apply</span>
            </div>
            <div className="trigs-list">
              <TrigToggle icon={Gauge} name="Mileage" sub="Replace on distance" on={t.miles!.on} onToggle={() => tog('miles')}>
                <span>every</span>
                <input className="input-sm" value={t.miles!.val} onChange={(e) => sv('miles', e.target.value)} inputMode="numeric" />
                <span>mi</span>
              </TrigToggle>
              <TrigToggle icon={CalendarDays} name="Time" sub="Replace on a calendar interval" on={t.time!.on} onToggle={() => tog('time')}>
                <span>every</span>
                <input className="input-sm" value={t.time!.val} onChange={(e) => sv('time', e.target.value)} inputMode="numeric" />
                <select className="select-sm" value={t.time!.unit} onChange={(e) => su('time', e.target.value)}>
                  {TIME_UNITS.map((u) => (
                    <option key={u}>{u}</option>
                  ))}
                </select>
              </TrigToggle>
              <TrigToggle icon={Flag} name="Passes" sub="Replace on a pass count" on={t.passes!.on} onToggle={() => tog('passes')}>
                <span>every</span>
                <input className="input-sm" value={t.passes!.val} onChange={(e) => sv('passes', e.target.value)} inputMode="numeric" />
                <span>passes</span>
              </TrigToggle>
              <TrigToggle icon={Timer} name="Events" sub="Replace every N race events" on={t.events!.on} onToggle={() => tog('events')}>
                <span>every</span>
                <input className="input-sm" value={t.events!.val} onChange={(e) => sv('events', e.target.value)} inputMode="numeric" />
                <span>events</span>
              </TrigToggle>
              <TrigToggle
                icon={CircleDashed}
                name="Inspect by hand"
                sub="No metric — just check on a window"
                on={t.inspect!.on}
                onToggle={() => tog('inspect')}
              >
                <span>every</span>
                <input className="input-sm" value={t.inspect!.val} onChange={(e) => sv('inspect', e.target.value)} inputMode="numeric" />
                <span>days</span>
              </TrigToggle>
            </div>

            {metricRule || inspectRule ? (
              <div className="rule-card">
                <div className="rule-h">
                  <Zap size={12} /> Resulting rule
                </div>
                {metricRule && <div className="rule-line">{metricRule}</div>}
                {inspectRule && (
                  <div className="rule-line rule-inspect">
                    <CircleDashed size={11} /> {inspectRule}
                  </div>
                )}
              </div>
            ) : (
              <div className="rule-empty">
                {p.safety
                  ? 'Optional for safety gear — it comes due on the recert date below.'
                  : 'Pick at least one check condition above.'}
              </div>
            )}

            <div className="form-sec">Starting point</div>
            <Field label="Last serviced / installed" hint="counters run forward from here">
              <input className="input" type="date" value={p.last} onChange={(e) => set('last', e.target.value)} />
            </Field>

            <div className="safety-wrap">
              <TrigToggle
                icon={ShieldCheck}
                name="This is safety gear"
                sub="Adds an expiration date + expiry notices"
                on={p.safety}
                onToggle={() => set('safety', !p.safety)}
                noInput
              />
              {p.safety && (
                <div className="safety-fields">
                  <Field label="Expiration / recert date">
                    <input className="input" type="date" value={p.expiry} onChange={(e) => set('expiry', e.target.value)} />
                  </Field>
                  <Field label="Spec" hint="e.g., SFI 27.1 · Snell SA2020">
                    <input className="input" value={p.spec} onChange={(e) => set('spec', e.target.value)} placeholder="SFI 27.1" />
                  </Field>
                  <div className="reminder-note">
                    <Bell size={12} /> Flagged at 60 and 30 days before expiry, and emailed to the garage.
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* The form's own rules, shown before a doomed request is sent. */}
        {issues.length > 0 && (
          <div className="rule-empty">
            {issues.map((i) => (
              <div key={i}>{i}</div>
            ))}
          </div>
        )}
        {save.isError && <div className="field-err">{errorMessage(save.error)}</div>}

        <button className="sheet-save-lg" onClick={submit} disabled={issues.length > 0 || busy}>
          {save.isPending ? 'Saving…' : editing ? 'Save changes' : `Add ${p.catalog ? 'catalog part' : 'part'} to ${owner.label}`}
        </button>

        {/* Delete lives only in the edit sheet, behind a confirming second tap:
            it removes the part from the forecast and takes its service history
            with it (routes/items.ts), so it is not a stray-click action. */}
        {editing && (
          <div className="danger-zone">
            {del.isError && <div className="field-err">{errorMessage(del.error)}</div>}
            {confirmDelete ? (
              <div className="danger-confirm">
                <span>Delete {item.name}? This also removes its service history.</span>
                <div className="danger-actions">
                  <button className="act" onClick={() => setConfirmDelete(false)} disabled={busy}>
                    Keep
                  </button>
                  <button className="act act-danger" onClick={remove} disabled={busy}>
                    {del.isPending ? 'Deleting…' : 'Delete part'}
                  </button>
                </div>
              </div>
            ) : (
              <button className="danger-link" onClick={() => setConfirmDelete(true)} disabled={busy}>
                <Trash2 size={13} /> Delete this part
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
